import React, { useEffect, useState } from 'react';
import { cqAPI } from '../../lib/Auth';
import {
  Box, Button, Text, Center, VStack, useColorMode, Flex,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalFooter, ModalBody, ModalCloseButton,
  Input, useToast, useDisclosure, HStack, Icon, IconButton, Grid, GridItem
} from '@chakra-ui/react';
import { AddIcon } from "@chakra-ui/icons";
import { FaCircle } from 'react-icons/fa';

const ReaderRow = ({ readers, handleMakeDefault }) => {
  return readers.map((reader) => (
    <Grid
      templateColumns="repeat(4, 1fr) auto"
      gap={4}
      p={2}
      key={reader.id}
      alignItems="center"
    >
      <GridItem>
        <Text fontSize="sm">{reader.label}</Text>
      </GridItem>
      <GridItem>
        <Text fontSize="sm">{reader.serial_number}</Text>
      </GridItem>
      <GridItem>
        <Text fontSize="sm">{reader.device_type}</Text>
      </GridItem>
      <GridItem>
        <Icon as={FaCircle} color={reader.status === 'online' ? 'green.500' : 'red.500'} />
      </GridItem>
      {!reader.default && (
        <GridItem>
          <Button size="sm" onClick={() => handleMakeDefault(reader.id)}>Make default</Button>
        </GridItem>
      )}
    </Grid>
  ));
};

function StripeConnect() {
  const [isConnected, setIsConnected] = useState(false);
  const [link, setLink] = useState('');
  const [label, setLabel] = useState('');
  const [registrationCode, setRegistrationCode] = useState('');
  const [readers, setReaders] = useState([]);
  const { colorMode } = useColorMode();
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const getStatus = async () => {
    try {
      const response = await cqAPI.get('/settings/stripe');
      if (response.data?.connected) {
        setIsConnected(true);
        setReaders(response.data?.readers);
      } else {
        setIsConnected(false);
        setLink(response.data?.link);
      }
    } catch (error) {
      console.error('Failed to fetch Stripe status', error);
    }
  };

  const handleRegister = async () => {
    if (!label || !registrationCode) {
      toast({
        title: "Error",
        description: "Both fields are required",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }
    try {
      const response = await cqAPI.post('/payments/register_reader', { label, registrationCode });
      if (response.type === 'error') {
        toast({
          title: "Error",
          description: response.msg,
          status: "error",
          duration: 5000,
          isClosable: true,
        });
        return;
      }
      toast({
        title: "Success",
        description: "Reader registered successfully",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
      setRegistrationCode('');
      setLabel('');
      getStatus(); // Refresh readers list after successful registration
      onClose();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to register reader",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      console.error('Failed to register reader', error);
    }
  };

  const handleMakeDefault = async (id) => {
    const response = await cqAPI.post('/payments/make_default_reader', { id });
    if (response.type === 'error'){
      toast({
        title: "Error",
        description: response.msg,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }
    getStatus(); // Refresh readers list after setting default
  };

  useEffect(() => {
    getStatus();
  }, []);

  return (
    <>
      {isConnected ? (
        <Box
          p={4}
          overflowY="auto"
          maxHeight="100vh"
          width="100%"
          position="relative"
        >
          <Flex
            p={4}
            overflowY="auto"
            maxHeight="100vh"
            padding="8px 24px 24px 24px"
            height="95vh"
            bgColor={colorMode === "light" ? "#FFFFFF" : "#212330"}
            borderRadius="16px"
            flexDirection="column"
          >
            <Flex justifyContent="flex-end" mb={4}>
              <Button leftIcon={<AddIcon />} onClick={onOpen}>
                Add Reader
              </Button>
            </Flex>
            <ReaderRow readers={readers} handleMakeDefault={handleMakeDefault} />
          </Flex>
        </Box>
      ) : (
        <Center height="100vh" width="100%">
          <VStack spacing={4}>
            <Text>Connect your Stripe account to be able to request payments from your patients over SMS and email.</Text>
            <Button colorScheme="blue" as="a" href={link}>
              Connect to Stripe
            </Button>
          </VStack>
        </Center>
      )}
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Add Reader</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <Input
                placeholder="Label"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
              />
              <Input
                placeholder="Registration Code"
                value={registrationCode}
                onChange={(e) => setRegistrationCode(e.target.value)}
              />
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" onClick={onClose}>Cancel</Button>
            <Button colorScheme="blue" onClick={handleRegister}>Register</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}

export default StripeConnect;
